import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Download, ArrowRight, ArrowLeft, AlertCircle, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import ResourcesSection from '../components/ResourcesSection';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResultsPage = () => {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  const [result, setResult] = useState(null);
  const [testInfo, setTestInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const response = await axios.get(`${API}/results/${resultId}`);
        setResult(response.data);
        
        // Fetch test info
        const testResponse = await axios.get(`${API}/tests/${response.data.test_id}?language=${language}`);
        setTestInfo(testResponse.data);
      } catch (error) {
        console.error('Error fetching result:', error);
        toast.error(t('common.error'));
        navigate('/tests');
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [resultId, language, navigate, t]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/results/${resultId}/pdf?language=${language}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mindful-sanctuary-${resultId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'it' ? 'PDF scaricato!' : 'PDF downloaded!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(t('common.error'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getSeverityIcon = (severity) => {
    const icons = {
      minimal: CheckCircle,
      low: CheckCircle,
      normal: CheckCircle,
      high: CheckCircle, // for wellbeing
      mild: AlertTriangle,
      moderate: AlertTriangle,
      moderately_severe: XCircle,
      severe: XCircle,
      extremely_severe: XCircle,
    };
    return icons[severity] || Info;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      minimal: '#4A7C59',
      low: '#4A7C59',
      normal: '#4A7C59',
      high: '#C05746', // For stress, but green for wellbeing
      mild: '#E09F3E',
      moderate: '#E09F3E',
      moderately_severe: '#C05746',
      severe: '#C05746',
      extremely_severe: '#C05746',
    };
    return colors[severity] || '#5D8AA8';
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!result || !testInfo) return null;

  const maxScore = testInfo.options 
    ? Math.max(...testInfo.options.map(o => o.value)) * testInfo.questions.length
    : 100;
  
  const scorePercentage = (result.total_score / maxScore) * 100;

  // Prepare chart data for subscales
  const subscaleData = result.subscales 
    ? Object.entries(result.subscales).map(([key, value]) => ({
        name: key.charAt(0).toUpperCase() + key.slice(1),
        value,
        fullMark: 42 // Max for DASS-21 subscales
      }))
    : null;

  const barChartData = result.subscales
    ? Object.entries(result.subscales).map(([key, value]) => ({
        name: key.charAt(0).toUpperCase() + key.slice(1),
        score: value,
        severity: result.interpretation[key]?.severity || 'normal'
      }))
    : null;

  const SeverityIcon = getSeverityIcon(result.severity === 'see_subscales' ? 'moderate' : result.severity);

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="results-page">
      <div className="container-narrow">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {language === 'it' ? 'Indietro' : 'Back'}
        </Button>
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-primary mb-2" data-testid="results-title">
            {t('results.title')}
          </h1>
          <p className="text-muted-foreground">
            {testInfo.name}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {new Date(result.created_at).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </motion.div>

        {/* Score Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className="bg-white border-0 shadow-medium rounded-3xl overflow-hidden mb-6" data-testid="score-card">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-lg font-medium text-muted-foreground">
                {t('results.score')}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <div className="relative inline-flex items-center justify-center mb-4">
                <svg className="w-40 h-40 transform -rotate-90">
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    fill="none"
                    stroke="#F2F4EF"
                    strokeWidth="12"
                  />
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    fill="none"
                    stroke={getSeverityColor(result.severity === 'see_subscales' ? 'moderate' : result.severity)}
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={`${(scorePercentage / 100) * 440} 440`}
                    className="transition-all duration-1000 ease-out"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl font-bold text-primary font-mono" data-testid="total-score">
                    {result.total_score}
                  </span>
                  <span className="text-sm text-muted-foreground">/ {maxScore}</span>
                </div>
              </div>

              {/* Severity Badge */}
              {result.severity !== 'see_subscales' && result.interpretation?.label && (
                <div 
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                    result.severity === 'minimal' || result.severity === 'low' || result.severity === 'normal'
                      ? 'bg-green-50 text-green-700'
                      : result.severity === 'mild' || result.severity === 'moderate'
                      ? 'bg-amber-50 text-amber-700'
                      : 'bg-red-50 text-red-700'
                  }`}
                  data-testid="severity-badge"
                >
                  <SeverityIcon className="w-5 h-5" />
                  <span className="font-medium">{result.interpretation.label}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Subscales Charts (for DASS-21 and similar) */}
        {subscaleData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="bg-white border-0 shadow-medium rounded-3xl overflow-hidden mb-6" data-testid="subscales-card">
              <CardHeader>
                <CardTitle className="text-xl font-serif text-primary">
                  {t('results.subscales')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Radar Chart */}
                <div className="h-64 mb-6">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={subscaleData}>
                      <PolarGrid stroke="#E2E8F0" />
                      <PolarAngleAxis 
                        dataKey="name" 
                        tick={{ fill: '#5C7566', fontSize: 12 }}
                      />
                      <PolarRadiusAxis 
                        angle={30} 
                        domain={[0, 42]} 
                        tick={{ fill: '#8FA396', fontSize: 10 }}
                      />
                      <Radar
                        name="Score"
                        dataKey="value"
                        stroke="#2C4F3E"
                        fill="#2C4F3E"
                        fillOpacity={0.3}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>

                {/* Bar Chart */}
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barChartData} layout="vertical">
                      <XAxis type="number" domain={[0, 42]} tick={{ fill: '#8FA396', fontSize: 12 }} />
                      <YAxis type="category" dataKey="name" tick={{ fill: '#5C7566', fontSize: 12 }} width={100} />
                      <Tooltip />
                      <Bar dataKey="score" radius={[0, 8, 8, 0]}>
                        {barChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getSeverityColor(entry.severity)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Subscale Details */}
                <div className="grid gap-3 mt-6">
                  {Object.entries(result.interpretation).map(([scale, data]) => (
                    <div 
                      key={scale}
                      className="flex items-center justify-between p-4 bg-secondary/30 rounded-2xl"
                      data-testid={`subscale-${scale}`}
                    >
                      <div>
                        <span className="font-medium text-primary capitalize">{scale}</span>
                        <span className="text-muted-foreground ml-2">({data.score})</span>
                      </div>
                      <span 
                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                          data.severity === 'normal' ? 'bg-green-100 text-green-700' :
                          data.severity === 'mild' ? 'bg-yellow-100 text-yellow-700' :
                          data.severity === 'moderate' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }`}
                      >
                        {data.label}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Interpretation (for simple tests) */}
        {result.severity !== 'see_subscales' && result.interpretation?.description && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="bg-white border-0 shadow-medium rounded-3xl overflow-hidden mb-6" data-testid="interpretation-card">
              <CardHeader>
                <CardTitle className="text-xl font-serif text-primary">
                  {t('results.interpretation')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed" data-testid="interpretation-text">
                  {result.interpretation.description}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Resources Section */}
        <ResourcesSection 
          testCategory={testInfo.category}
          severity={result.severity}
          subscaleResults={result.severity === 'see_subscales' ? result.interpretation : null}
        />

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card className="bg-amber-50 border-amber-200 rounded-3xl overflow-hidden mb-8 mt-8" data-testid="disclaimer-card">
            <CardContent className="flex gap-4 p-6">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 leading-relaxed">
                {t('results.disclaimer')}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Button
            size="lg"
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-medium"
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            data-testid="download-pdf-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            {downloadingPdf 
              ? (language === 'it' ? 'Scaricamento...' : 'Downloading...') 
              : t('results.downloadPdf')}
          </Button>
          
          <Button
            size="lg"
            variant="outline"
            className="rounded-full"
            onClick={() => navigate('/tests')}
            data-testid="take-another-btn"
          >
            {t('results.takeAnother')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default ResultsPage;
